import { useState, useEffect, useRef, useMemo } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Avatar, Box, Button, Chip, CircularProgress,
  Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, IconButton, Paper, Stack, Tooltip, Typography,
  LinearProgress, Menu, MenuItem, TextField,
} from '@mui/material'
import {
  ArrowBack as BackIcon,
  ArrowForward as ArrowForwardIcon,
  Edit as EditIcon,
  AttachMoney as AmountIcon,
  CalendarToday as CalIcon,
  CalendarMonth as CalendarIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  EmojiEvents as WonIcon,
  StickyNote2 as NoteIcon,
  Task as TaskIcon,
  Timeline as TimelineIcon,
  Info as InfoIcon,
  Check as CheckIcon,
  OpenInNew as OpenInNewIcon,
  SwapHoriz as SwapIcon,
  Warning as WarningIcon,
  CheckCircle as DoneIcon,
  Schedule as PendingIcon,
  ContentCopy as CopyIcon,
  Link as LinkIcon,
  Apartment as OrgIcon,
  Category as LobIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  RadioButtonUnchecked as OpenIcon,
} from '@mui/icons-material'
import { toast } from 'react-toastify'
import { observer } from 'mobx-react-lite'
import { maskPhoneNumber } from '../utils/phoneUtils'
import axios from 'axios'

import { salesApi, SalesRecord } from '../lib/api/salesApi'
import { retentionApi, RetentionNote } from '../lib/api/retentionApi'
import { onboardingApi } from '../lib/api/onboardingApi'
import { getStatusStyle } from './SalesPage'
import { usePermission } from '../lib/hooks/usePermission'
import { TaskModal } from '../components/shared/TaskModal'
import type { AiRecencyCustomer } from './SalesCustomerRetentionPage'
import { dealsApi } from '../lib/api/dealsApi'
import { openSalesAppForRetentionCustomer } from '../lib/utils/salesRedirect'
import { themeColors } from '../app/layout/theme'
import { SideCard } from '../components/shared/SideCard'
import { InfoRow } from '../components/shared/DetailViewWrappers'
import { CommentItem, CommentInput, CommentsEmptyState } from '../components/shared/NotesSection'
import { EntityDetailTabs } from '../components/shared/EntityDetailTabs'
import { EmailHistorySection } from '../components/shared/EmailHistorySection'
import { SendEmailDrawer, SendEmailData } from '../components/drawers/SendEmailDrawer'
import { emailApi } from '../lib/api/emailApi'
import { HelpAssigneeSelector } from '../components/shared/HelpAssigneeSelector'
import { callStore } from '../lib/stores/callStore'
import { contactsApi, Contact } from '../lib/api/contactsApi'
import { usersAdminApi, AdminUser } from '../lib/api/usersAdminApi'
import RetentionTasksSection from '../components/retention/RetentionTasksSection'
import RetentionCallsSection from '../components/retention/RetentionCallsSection'
import RetentionEventsSection from '../components/retention/RetentionEventsSection'
import RetentionAttachmentsSection from '../components/retention/RetentionAttachmentsSection'
import { UniversalActivityTimeline } from '../components/shared/UniversalActivityTimeline'

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmt(iso: string | null | undefined) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
}

function fmtAmount(amount: number | null | undefined, currency = 'INR') {
  if (amount == null) return '—'
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: currency || 'INR', maximumFractionDigits: 0 }).format(amount)
}

function initials(name: string) {
  return name.split(' ').slice(0, 2).map(n => n[0]?.toUpperCase() ?? '').join('')
}

const AVATAR_COLORS = ['#0070d2', '#2e7d32', '#7b1fa2', '#f57c00', '#c62828', '#00838f']
function avatarColor(name: string) {
  let h = 0; for (const c of name) h = c.charCodeAt(0) + ((h << 5) - h)
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length]
}

// ── Sales Stage Progress Bar ──────────────────────────────────────────────────

function SalesStageProgressBar({
  salesRecord,
  isChanging,
}: {
  salesRecord: SalesRecord
  isChanging?: boolean
}) {
  const status = salesRecord.status || 'New'
  const activeSteps = ['New', 'Onboarded', 'Won']
  
  const currentIdx = activeSteps.indexOf(status)
  const isTerminal = status === 'Won' || status === 'Lost'
  const isWon = status === 'Won'
  const isLost = status === 'Lost'

  let pct = 20
  if (status === 'Onboarded') pct = 60
  else if (status === 'Won' || status === 'Lost') pct = 100

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, alignItems: 'center' }}>
        <Typography fontSize="12px" fontWeight={600} color="text.secondary">
          Pipeline Progress
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          {isChanging && (
            <CircularProgress size={10} sx={{ color: themeColors.primary }} />
          )}
          <Typography fontSize="12px" fontWeight={700} color={isLost ? '#dc2626' : (isWon ? '#16a34a' : themeColors.primary)}>
            {status} ({pct}%)
          </Typography>
        </Box>
      </Box>

      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{
          height: 6,
          borderRadius: 3,
          bgcolor: '#e5e7eb',
          '& .MuiLinearProgress-bar': {
            borderRadius: 3,
            bgcolor: isLost
              ? '#ef4444'
              : isWon
                ? '#16a34a'
                : themeColors.primary,
            transition: 'width 0.4s ease',
          },
        }}
      />

      <Box sx={{ display: 'flex', gap: 0.5, mt: 1.5, flexWrap: 'wrap' }}>
        {activeSteps.map((s, i) => {
          const isCurrent = s === status
          const isPast = isTerminal || (!isCurrent && currentIdx >= 0 && i < currentIdx)
          const isFuture = !isTerminal && !isCurrent && currentIdx >= 0 && i > currentIdx

          let color = '#6366f1'
          let bgcolor = '#6366f118'
          let border = '1px solid #6366f144'

          if (s === 'New') {
            color = '#0369a1'
            bgcolor = '#e0f2fe'
            border = '1px solid #bae6fd'
          } else if (s === 'Onboarded') {
            color = '#c2410c'
            bgcolor = '#fff7ed'
            border = '1px solid #ffedd5'
          } else if (s === 'Won') {
            color = '#16a34a'
            bgcolor = '#f0fdf4'
            border = '1px solid #bbf7d0'
          }

          return (
            <Tooltip
              key={s}
              title={
                isCurrent
                  ? `Current: ${s}`
                  : isPast
                    ? `Completed: ${s}`
                    : s
              }
              arrow
            >
              <Box
                sx={{
                  flex: 1,
                  minWidth: 50,
                  textAlign: 'center',
                  py: 0.5,
                  px: 0.5,
                  borderRadius: '4px',
                  position: 'relative',
                  ...(isCurrent && {
                    cursor: 'default',
                    bgcolor: bgcolor,
                    border: border,
                    transform: 'scaleY(1.05)',
                    boxShadow: `0 2px 8px ${color}30`,
                  }),
                  ...(isPast && !isCurrent && {
                    cursor: 'default',
                    bgcolor: '#f0fdf4',
                    border: '1.5px solid #86efac',
                    opacity: 0.75,
                  }),
                  ...(isFuture && {
                    cursor: 'default',
                    bgcolor: '#f8fafc',
                    border: '1.5px solid #e2e8f0',
                  }),
                }}
              >
                <Typography
                  fontSize="9px"
                  fontWeight={isCurrent ? 700 : 500}
                  color={isCurrent ? color : (isPast ? '#16a34a' : 'text.secondary')}
                  noWrap
                >
                  {s}
                </Typography>
              </Box>
            </Tooltip>
          )
        })}
      </Box>
    </Box>
  )
}

// ── Select Contact Modals ────────────────────────────────────────────────────

function SelectEmailContactModal({
  open, customerId, customerName, customerEmail, onClose, onSelect,
}: { open: boolean; customerId: string | null; customerName?: string | null; customerEmail?: string | null; onClose: () => void; onSelect: (emails: string[]) => void }) {
  const [selected, setSelected] = useState<Set<string>>(new Set())

  const { data, isLoading } = useQuery({
    queryKey: ['contacts-for-email', customerId],
    queryFn: () => contactsApi.getAll({ customerId: customerId!, limit: 100 }),
    enabled: open && !!customerId && !customerId.startsWith('mock-'),
  })
  const contacts: Contact[] = data?.items ?? []
  const withEmail = contacts.filter(c => c.email)

  const allRecipients = useMemo(() => {
    const list: { id: string; name: string; email: string; role?: string }[] = []
    if (customerEmail) {
      list.push({
        id: 'primary-customer-email',
        name: customerName || 'Customer',
        email: customerEmail,
        role: 'Retention Customer',
      })
    }
    withEmail.forEach(c => {
      if (c.email && (!customerEmail || c.email.toLowerCase() !== customerEmail.toLowerCase())) {
        list.push({
          id: c.id,
          name: c.name,
          email: c.email,
          role: c.role || undefined,
        })
      }
    })
    return list
  }, [customerEmail, customerName, withEmail])

  const toggle = (email: string) => {
    setSelected(prev => { const next = new Set(prev); next.has(email) ? next.delete(email) : next.add(email); return next })
  }
  const handleConfirm = () => {
    if (selected.size === 0) { toast.error('Select at least one recipient'); return }
    onSelect([...selected]); setSelected(new Set())
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '14px' } }}>
      <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <EmailIcon sx={{ fontSize: 18, color: themeColors.primary }} />
        <Typography fontWeight={700} fontSize="15px">Select Recipients</Typography>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>
        ) : allRecipients.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <EmailIcon sx={{ fontSize: 36, color: '#e0e0e0', mb: 1 }} />
            <Typography fontSize="13px" color="text.secondary">No contacts with email addresses found.</Typography>
          </Box>
        ) : (
          <Stack spacing={1}>
            {allRecipients.map(c => {
              const isOn = selected.has(c.email)
              return (
                <Box key={c.id} onClick={() => toggle(c.email)}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.25, borderRadius: '10px', cursor: 'pointer', border: `1.5px solid ${isOn ? themeColors.primary : '#e5e7eb'}`, bgcolor: isOn ? `${themeColors.primary}08` : '#fafafa', transition: 'all 0.15s', '&:hover': { borderColor: themeColors.primary, bgcolor: `${themeColors.primary}06` } }}>
                  <Avatar sx={{ width: 32, height: 32, fontSize: '12px', fontWeight: 700, bgcolor: isOn ? themeColors.primary : '#9ca3af', flexShrink: 0 }}>
                    {c.name.split(' ').map((w: string) => w[0]).slice(0, 2).join('').toUpperCase()}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography fontSize="13px" fontWeight={600} noWrap>{c.name}</Typography>
                    <Typography fontSize="11.5px" color="text.secondary" noWrap>{c.email}</Typography>
                    {c.role && <Typography fontSize="10.5px" color="text.disabled">{c.role}</Typography>}
                  </Box>
                  {isOn && <CheckIcon sx={{ fontSize: 16, color: themeColors.primary, flexShrink: 0 }} />}
                </Box>
              )
            })}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={onClose} variant="outlined" sx={{ borderRadius: '8px', textTransform: 'none' }}>Cancel</Button>
        <Button onClick={handleConfirm} variant="contained" disabled={selected.size === 0 || allRecipients.length === 0}
          sx={{ borderRadius: '8px', textTransform: 'none', bgcolor: themeColors.primary }}>
          Compose Email
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function SelectCallContactModal({
  open, customerId, customerName, customerPhone, onClose, onSelect,
}: { open: boolean; customerId: string | null; customerName?: string | null; customerPhone?: string | null; onClose: () => void; onSelect: (contact: Partial<Contact>) => void }) {
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['contacts-for-call', customerId],
    queryFn: () => contactsApi.getAll({ customerId: customerId!, limit: 100 }),
    enabled: open && !!customerId && !customerId.startsWith('mock-'),
  })
  const contacts: Contact[] = data?.items ?? []
  const withPhone = contacts.filter(c => c.phone)

  const allContacts = useMemo(() => {
    const list: { id: string; name: string; phone: string; role?: string }[] = []
    if (customerPhone) {
      list.push({
        id: 'primary-customer-phone',
        name: customerName || 'Customer',
        phone: customerPhone,
        role: 'Retention Customer',
      })
    }
    withPhone.forEach(c => {
      if (c.phone && (!customerPhone || c.phone.replace(/[^0-9]/g, '') !== customerPhone.replace(/[^0-9]/g, ''))) {
        list.push({
          id: c.id,
          name: c.name,
          phone: c.phone,
          role: c.role || undefined,
        })
      }
    })
    return list
  }, [customerPhone, customerName, withPhone])

  const handleConfirm = () => {
    if (!selectedId) { toast.error('Select a contact to call'); return }
    const contact = allContacts.find(c => c.id === selectedId)
    if (contact) onSelect(contact as Contact)
    setSelectedId(null)
  }
  const handleClose = () => { setSelectedId(null); onClose() }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '14px' } }}>
      <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <PhoneIcon sx={{ fontSize: 18, color: themeColors.primary }} />
        <Typography fontWeight={700} fontSize="15px">Select Contact to Call</Typography>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>
        ) : allContacts.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <PhoneIcon sx={{ fontSize: 36, color: '#e0e0e0', mb: 1 }} />
            <Typography fontSize="13px" color="text.secondary">No contacts with phone numbers found.</Typography>
          </Box>
        ) : (
          <Stack spacing={1}>
            {allContacts.map(c => {
              const isOn = selectedId === c.id
              return (
                <Box key={c.id} onClick={() => setSelectedId(c.id)}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.25, borderRadius: '10px', cursor: 'pointer', border: `1.5px solid ${isOn ? themeColors.primary : '#e5e7eb'}`, bgcolor: isOn ? `${themeColors.primary}08` : '#fafafa', transition: 'all 0.15s', '&:hover': { borderColor: themeColors.primary, bgcolor: `${themeColors.primary}06` } }}>
                  <Avatar sx={{ width: 32, height: 32, fontSize: '12px', fontWeight: 700, bgcolor: isOn ? themeColors.primary : '#9ca3af', flexShrink: 0 }}>
                    {c.name.split(' ').map((w: string) => w[0]).slice(0, 2).join('').toUpperCase()}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography fontSize="13px" fontWeight={600} noWrap>{c.name}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <PhoneIcon sx={{ fontSize: 11, color: '#9ca3af' }} />
                      <Typography fontSize="11.5px" color="text.secondary" noWrap>{maskPhoneNumber(c.phone)}</Typography>
                    </Box>
                    {c.role && <Typography fontSize="10.5px" color="text.disabled">{c.role}</Typography>}
                  </Box>
                  {isOn && <CheckIcon sx={{ fontSize: 16, color: themeColors.primary, flexShrink: 0 }} />}
                </Box>
              )
            })}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={handleClose} variant="outlined" sx={{ borderRadius: '8px', textTransform: 'none' }}>Cancel</Button>
        <Button onClick={handleConfirm} variant="contained" disabled={!selectedId || allContacts.length === 0}
          sx={{ borderRadius: '8px', textTransform: 'none', bgcolor: themeColors.primary }}>
          Start Call
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// ── Reassign Owner Modal ──────────────────────────────────────────────────────

function ReassignOwnerModal({
  open, currentOwnerId, orgId, onClose, onSelect,
}: { open: boolean; currentOwnerId: string; orgId: string | null; onClose: () => void; onSelect: (user: AdminUser) => void }) {
  const [search, setSearch] = useState('')
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['users-for-assign', orgId ?? 'all'],
    queryFn: () => usersAdminApi.getAll({ pageSize: 1000, status: 'Active', ...(orgId ? { orgId } : {}) }),
    enabled: open,
    staleTime: 2 * 60_000,
  })
  const users: AdminUser[] = data?.items ?? []
  const filtered = users.filter(u => `${u.name} ${u.email}`.toLowerCase().includes(search.toLowerCase()))

  const handleConfirm = () => {
    const user = users.find(u => u.id === selectedId)
    if (!user) return
    onSelect(user); setSelectedId(null); setSearch('')
  }
  const handleClose = () => { setSelectedId(null); setSearch(''); onClose() }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '14px' } }}>
      <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <SwapIcon sx={{ fontSize: 18, color: themeColors.primary }} />
        <Typography fontWeight={700} fontSize="15px">Reassign Churn Customer</Typography>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, px: 1.25, py: 0.75, borderRadius: '8px', border: '1.5px solid #e5e7eb', bgcolor: '#fafafa', '&:focus-within': { borderColor: themeColors.primary } }}>
          <PersonIcon sx={{ fontSize: 15, color: '#9ca3af' }} />
          <Box component="input" value={search}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
            placeholder="Search by name or email…" autoFocus
            sx={{ flex: 1, border: 'none', outline: 'none', bgcolor: 'transparent', fontSize: '13px', color: 'text.primary' }} />
        </Box>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>
        ) : filtered.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <PersonIcon sx={{ fontSize: 36, color: '#e0e0e0', mb: 1 }} />
            <Typography fontSize="13px" color="text.secondary">No users found</Typography>
          </Box>
        ) : (
          <Stack spacing={0.75} sx={{ maxHeight: 280, overflowY: 'auto' }}>
            {filtered.map(u => {
              const isSelected = selectedId === u.id
              const isCurrent = u.id === currentOwnerId
              return (
                <Box key={u.id} onClick={() => !isCurrent && setSelectedId(u.id)}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.25, borderRadius: '10px', cursor: isCurrent ? 'default' : 'pointer', border: `1.5px solid ${isSelected ? themeColors.primary : isCurrent ? '#bbf7d0' : '#e5e7eb'}`, bgcolor: isSelected ? `${themeColors.primary}08` : isCurrent ? '#f0fdf4' : '#fafafa', transition: 'all 0.15s', '&:hover': !isCurrent ? { borderColor: themeColors.primary, bgcolor: `${themeColors.primary}06` } : {} }}>
                  <Avatar sx={{ width: 32, height: 32, fontSize: '12px', fontWeight: 700, bgcolor: isSelected ? themeColors.primary : isCurrent ? '#16a34a' : '#9ca3af', flexShrink: 0 }}>
                    {u.name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                      <Typography fontSize="13px" fontWeight={600} noWrap>{u.name}</Typography>
                      {u.lobName && (
                        <Chip label={u.lobName} size="small" sx={{ height: 16, fontSize: '9.5px', fontWeight: 500, bgcolor: '#f3f4f6', color: '#374151', '& .MuiChip-label': { px: '5px' } }} />
                      )}
                      {isCurrent && (
                        <Chip label="Current" size="small" sx={{ height: 16, fontSize: '9.5px', fontWeight: 700, bgcolor: '#dcfce7', color: '#16a34a', '& .MuiChip-label': { px: '5px' } }} />
                      )}
                    </Box>
                    <Typography fontSize="11.5px" color="text.secondary" noWrap>{u.email}</Typography>
                    {u.roleName && <Typography fontSize="10.5px" color="text.disabled" noWrap>{u.roleName}</Typography>}
                  </Box>
                  {isSelected && <CheckIcon sx={{ fontSize: 16, color: themeColors.primary, flexShrink: 0 }} />}
                  {isCurrent && !isSelected && <CheckIcon sx={{ fontSize: 16, color: '#16a34a', flexShrink: 0 }} />}
                </Box>
              )
            })}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={handleClose} variant="outlined" sx={{ borderRadius: '8px', textTransform: 'none' }}>Cancel</Button>
        <Button onClick={handleConfirm} variant="contained" disabled={!selectedId}
          sx={{ borderRadius: '8px', textTransform: 'none', bgcolor: themeColors.primary }}>
          Reassign
        </Button>
      </DialogActions>
    </Dialog>
  )
}


// ── Mock Components for Retention Customer Detail Page ────────────────────────

function MockCallsSection({
  calls,
  onLogCall,
  canModify = true,
}: {
  calls: any[]
  onLogCall: () => void
  canModify?: boolean
}) {
  if (calls.length === 0) {
    return (
      <Box>
        {canModify && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<PhoneIcon sx={{ fontSize: 15 }} />}
              onClick={onLogCall}
              sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, fontSize: 13 }}
            >
              Log Call
            </Button>
          </Box>
        )}
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <PhoneIcon sx={{ fontSize: 36, color: 'rgba(0,0,0,0.1)', mb: 1, display: 'block', mx: 'auto' }} />
          <Typography fontSize="13px" color="text.secondary">No calls logged yet</Typography>
          {canModify && (
            <Typography fontSize="11.5px" color="text.disabled" mt={0.5}>
              Click "Log Call" to start a call for this sales record
            </Typography>
          )}
        </Box>
      </Box>
    )
  }

  const connected = calls.filter(c => c.status === 'CONNECTED').length
  const notAnswered = calls.filter(c => c.status === 'NOT_ANSWERED' || c.status === 'BUSY').length
  const avgDuration = calls.filter(c => c.durationSeconds).length
    ? Math.round(calls.reduce((s, c) => s + (c.durationSeconds ?? 0), 0) / calls.filter(c => c.durationSeconds).length)
    : null

  const formatDuration = (sec: number) => {
    const m = Math.floor(sec / 60)
    const s = sec % 60
    return `${m}:${s < 10 ? '0' : ''}${s}`
  }

  return (
    <Box>
      {canModify && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<PhoneIcon sx={{ fontSize: 15 }} />}
            onClick={onLogCall}
            sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, fontSize: 13 }}
          >
            Log Call
          </Button>
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Chip label={`${calls.length} total`} size="small" sx={{ fontSize: '11px', bgcolor: '#f3f4f6', fontWeight: 600 }} />
        <Chip label={`${connected} connected`} size="small" sx={{ fontSize: '11px', bgcolor: '#dcfce7', color: '#166534', fontWeight: 600 }} />
        {notAnswered > 0 && (
          <Chip label={`${notAnswered} missed`} size="small" sx={{ fontSize: '11px', bgcolor: '#fef3c7', color: '#92400e', fontWeight: 600 }} />
        )}
        {avgDuration && (
          <Chip label={`avg ${formatDuration(avgDuration)}`} size="small" sx={{ fontSize: '11px', bgcolor: '#eff6ff', color: '#1d4ed8', fontWeight: 600 }} />
        )}
      </Box>

      <Stack divider={<Divider sx={{ borderColor: '#f3f4f6' }} />}>
        {calls.map(call => (
          <Box key={call.id} sx={{ display: 'flex', gap: 1.5, px: 0.5, py: 1.25, alignItems: 'flex-start' }}>
            <Box sx={{ width: 34, height: 34, borderRadius: '10px', bgcolor: 'rgba(22, 163, 74, 0.15)', border: '1px solid rgba(22, 163, 74, 0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mt: '2px' }}>
              <PhoneIcon sx={{ fontSize: 16, color: '#16a34a', alignSelf: 'center' }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.4, flexWrap: 'wrap' }}>
                <Chip label="Connected" size="small" sx={{ height: 18, fontSize: '10px', fontWeight: 700, bgcolor: '#dcfce7', color: '#166534', border: '1px solid rgba(22, 163, 74, 0.3)' }} />
                {call.outcome && (
                  <Chip label={call.outcome} size="small" sx={{ height: 18, fontSize: '10px', fontWeight: 700, bgcolor: '#eff6ff', color: '#1d4ed8' }} />
                )}
                <Box sx={{ flex: 1 }} />
                <Typography fontSize="10px" color="text.disabled">↗ Outgoing</Typography>
              </Box>
              <Typography fontSize="11.5px" color="text.secondary">
                {new Date(call.startedAt).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </Typography>
              {call.notes && (
                <Typography fontSize="12px" color="text.secondary" sx={{ mt: 0.25, fontStyle: 'italic' }}>
                  "{call.notes}"
                </Typography>
              )}
              <Typography fontSize="10.5px" color="text.disabled" mt={0.5}>
                by {call.initiatedBy?.name || 'Current User'}
              </Typography>
            </Box>
          </Box>
        ))}
      </Stack>
    </Box>
  )
}

function MockEmailsSection({
  emails,
  onCompose,
  canModify = true,
}: {
  emails: any[]
  onCompose: () => void
  canModify?: boolean
}) {
  if (emails.length === 0) {
    return (
      <Box>
        {canModify && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<EmailIcon sx={{ fontSize: 15 }} />}
              onClick={onCompose}
              sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, fontSize: 13 }}
            >
              Compose Email
            </Button>
          </Box>
        )}
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <EmailIcon sx={{ fontSize: 36, color: 'rgba(0,0,0,0.1)', mb: 1, display: 'block', mx: 'auto' }} />
          <Typography fontSize="13px" color="text.secondary">No emails sent yet</Typography>
        </Box>
      </Box>
    )
  }

  return (
    <Box>
      {canModify && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<EmailIcon sx={{ fontSize: 15 }} />}
            onClick={onCompose}
            sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, fontSize: 13 }}
          >
            Compose Email
          </Button>
        </Box>
      )}

      <Stack divider={<Divider sx={{ borderColor: '#f3f4f6' }} />}>
        {emails.map(email => (
          <Box key={email.id} sx={{ py: 1.5 }}>
            <Typography fontSize="13px" fontWeight={700} color="text.primary">{email.subject}</Typography>
            <Typography fontSize="11px" color="text.secondary" sx={{ mb: 1 }}>
              To: {email.to.join(', ')} · Sent: {new Date(email.sentAt).toLocaleString('en-IN')}
            </Typography>
            <Box
              sx={{ p: 1, border: '1px solid #e5e7eb', borderRadius: '8px', bgcolor: '#f9fafb', fontSize: '12.5px', color: 'text.secondary', maxHeight: 100, overflowY: 'auto' }}
              dangerouslySetInnerHTML={{ __html: email.body }}
            />
          </Box>
        ))}
      </Stack>
    </Box>
  )
}

function MockTasksSection({
  tasks,
  onToggle,
  onEdit,
  onDelete,
  onAdd,
  canModify = true,
}: {
  tasks: any[]
  onToggle: (id: string) => void
  onEdit: (task: any) => void
  onDelete: (id: string) => void
  onAdd: () => void
  canModify?: boolean
}) {
  const open = tasks.filter(t => t.status !== 'DONE')
  const done = tasks.filter(t => t.status === 'DONE')

  const PRIORITY_COLORS: Record<string, { bg: string; color: string }> = {
    LOW: { bg: '#f0fdf4', color: '#16a34a' },
    MEDIUM: { bg: '#fefce8', color: '#ca8a04' },
    HIGH: { bg: '#fff7ed', color: '#ea580c' },
    URGENT: { bg: '#fef2f2', color: '#dc2626' },
  }

  const RenderTask = (task: any) => {
    const isDone = task.status === 'DONE'
    const pc = PRIORITY_COLORS[task.priority] ?? PRIORITY_COLORS.MEDIUM
    return (
      <Box key={task.id} sx={{ p: 1.5, border: `1px solid ${themeColors.border}`, borderRadius: '10px', bgcolor: isDone ? '#f9fafb' : '#fff', opacity: isDone ? 0.75 : 1, transition: 'all 0.15s' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          <IconButton size="small" onClick={() => onToggle(task.id)} disabled={!canModify} sx={{ color: isDone ? '#16a34a' : '#9ca3af', mt: '-2px', p: 0.25 }}>
            {isDone ? <DoneIcon sx={{ fontSize: 20, color: '#16a34a' }} /> : <OpenIcon sx={{ fontSize: 20, color: '#d1d5db' }} />}
          </IconButton>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography fontSize="13px" fontWeight={600} sx={{ textDecoration: isDone ? 'line-through' : 'none', color: isDone ? 'text.secondary' : 'text.primary' }}>{task.title}</Typography>
            {task.description && <Typography fontSize="12px" color="text.secondary" sx={{ mt: 0.25, lineHeight: 1.5 }}>{task.description}</Typography>}
            <Box sx={{ display: 'flex', gap: 0.75, mt: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
              <Chip label={task.priority} size="small" sx={{ height: 18, fontSize: '10px', fontWeight: 700, bgcolor: pc.bg, color: pc.color, borderRadius: '4px', '& .MuiChip-label': { px: '6px' } }} />
              {task.dueDate && (
                <Typography fontSize="10.5px" sx={{ color: '#6b7280' }}>Due: {new Date(task.dueDate).toLocaleDateString('en-IN')}</Typography>
              )}
            </Box>
          </Box>
          {canModify && (
            <Box sx={{ display: 'flex', gap: 0.25, flexShrink: 0 }}>
              <IconButton size="small" onClick={() => onEdit(task)} sx={{ color: '#9ca3af' }}><EditIcon sx={{ fontSize: 15 }} /></IconButton>
              <IconButton size="small" onClick={() => onDelete(task.id)} sx={{ color: '#9ca3af' }}><DeleteIcon sx={{ fontSize: 15 }} /></IconButton>
            </Box>
          )}
        </Box>
      </Box>
    )
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography fontSize="13px" color="text.secondary">{open.length} open · {done.length} completed</Typography>
        {canModify && (
          <Button size="small" startIcon={<AddIcon />} variant="outlined" onClick={onAdd} sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, fontSize: '12px', borderColor: themeColors.border, color: 'text.primary' }}>
            Add Task
          </Button>
        )}
      </Box>

      {tasks.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6, bgcolor: '#f9fafb', borderRadius: '12px', border: '1px dashed #e5e7eb' }}>
          <TaskIcon sx={{ fontSize: 32, color: '#d1d5db', mb: 1 }} />
          <Typography fontSize="13px" color="text.secondary">No tasks yet. Add one to track execution.</Typography>
        </Box>
      ) : (
        <Stack spacing={1}>
          {open.map(task => RenderTask(task))}
          {done.length > 0 && (
            <>
              <Typography fontSize="11px" fontWeight={700} color="text.disabled" textTransform="uppercase" letterSpacing="0.5px" sx={{ pt: 1 }}>
                Completed ({done.length})
              </Typography>
              {done.map(task => RenderTask(task))}
            </>
          )}
        </Stack>
      )}
    </Box>
  )
}

function MockEventsSection({
  events,
  onCreate,
  createOpen,
  onCreateClose,
}: {
  events: any[]
  onCreate: (event: any) => void
  createOpen: boolean
  onCreateClose: () => void
}) {
  const [title, setTitle] = useState('')
  const [desc, setDesc] = useState('')
  const [date, setDate] = useState('')
  const [duration, setDuration] = useState(30)

  const handleSave = () => {
    if (!title.trim() || !date) {
      toast.error('Title and Date are required')
      return
    }
    onCreate({ title, description: desc, startedAt: new Date(date).toISOString(), durationMinutes: duration })
    setTitle(''); setDesc(''); setDate(''); setDuration(30)
    onCreateClose()
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography fontSize="13px" color="text.secondary">
          {events.length} meeting{events.length === 1 ? '' : 's'} scheduled
        </Typography>
        <Button size="small" startIcon={<AddIcon />} variant="outlined"
          onClick={onCreateClose}
          sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, fontSize: '12px', borderColor: themeColors.border, color: 'text.primary' }}>
          Schedule Meeting
        </Button>
      </Box>

      {events.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6, bgcolor: '#f9fafb', borderRadius: '12px', border: '1px dashed #e5e7eb' }}>
          <CalendarIcon sx={{ fontSize: 32, color: '#d1d5db', mb: 1 }} />
          <Typography fontSize="13px" color="text.secondary">No meetings scheduled yet</Typography>
        </Box>
      ) : (
        <Stack spacing={1.5}>
          {events.map(ev => (
            <Box key={ev.id} sx={{ p: 1.5, border: `1px solid ${themeColors.border}`, borderRadius: '10px', bgcolor: '#fff' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Typography fontSize="13px" fontWeight={600} color="text.primary">{ev.title}</Typography>
                <Chip label={`${ev.durationMinutes} mins`} size="small" sx={{ height: 18, fontSize: '10px', fontWeight: 600 }} />
              </Box>
              {ev.description && <Typography fontSize="12px" color="text.secondary" sx={{ mt: 0.5 }}>{ev.description}</Typography>}
              <Typography fontSize="11px" color="text.disabled" sx={{ mt: 1 }}>
                Scheduled: {new Date(ev.startedAt).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </Typography>
            </Box>
          ))}
        </Stack>
      )}

      <Dialog open={createOpen} onClose={onCreateClose} PaperProps={{ sx: { borderRadius: '14px', px: 1, py: 0.5 } }}>
        <DialogTitle sx={{ fontWeight: 800, fontSize: '16px', pb: 1 }}>Schedule Meeting</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1, width: 320 }}>
            <TextField label="Meeting Title" size="small" fullWidth value={title} onChange={e => setTitle(e.target.value)} required />
            <TextField label="Date & Time" type="datetime-local" size="small" fullWidth slotProps={{ inputLabel: { shrink: true } }} value={date} onChange={e => setDate(e.target.value)} required />
            <TextField label="Duration (minutes)" type="number" size="small" fullWidth value={duration} onChange={e => setDuration(Number(e.target.value))} />
            <TextField label="Description" size="small" fullWidth multiline rows={3} value={desc} onChange={e => setDesc(e.target.value)} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button onClick={onCreateClose} variant="outlined" size="small" sx={{ borderRadius: '8px', textTransform: 'none' }}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" size="small" sx={{ borderRadius: '8px', textTransform: 'none', bgcolor: themeColors.primary }}>Schedule</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

function MockAttachmentsSection({
  attachments,
  onUpload,
  onDelete,
}: {
  attachments: any[]
  onUpload: (file: File) => void
  onDelete: (id: string) => void
}) {
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      onUpload(file)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography fontSize="13px" color="text.secondary">
          {attachments.length} attachment{attachments.length === 1 ? '' : 's'}
        </Typography>
        <input type="file" ref={fileInputRef} onChange={handleFileChange} style={{ display: 'none' }} />
        <Button size="small" variant="outlined"
          onClick={() => fileInputRef.current?.click()}
          sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, fontSize: '12px', borderColor: themeColors.border, color: 'text.primary' }}>
          Upload File
        </Button>
      </Box>

      {attachments.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6, bgcolor: '#f9fafb', borderRadius: '12px', border: '1px dashed #e5e7eb' }}>
          <OpenInNewIcon sx={{ fontSize: 32, color: '#d1d5db', mb: 1 }} />
          <Typography fontSize="13px" color="text.secondary">No attachments uploaded yet</Typography>
        </Box>
      ) : (
        <Stack spacing={1}>
          {attachments.map(att => (
            <Box key={att.id} sx={{ p: 1.25, border: `1px solid ${themeColors.border}`, borderRadius: '10px', bgcolor: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography fontSize="12.5px" fontWeight={600} color="text.primary">{att.name}</Typography>
                <Typography fontSize="11px" color="text.disabled">{(att.size / 1024).toFixed(1)} KB · Uploaded just now</Typography>
              </Box>
              <IconButton size="small" onClick={() => onDelete(att.id)} sx={{ color: '#9ca3af' }}>
                <DeleteIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export interface SalesCustomerRetentionDetailPageProps {
  retentionRecordId?: string
  readOnly?: boolean
}

export const SalesCustomerRetentionDetailPage = observer(function SalesCustomerRetentionDetailPage({
  retentionRecordId: retentionRecordIdProp,
  readOnly: readOnlyProp = false,
}: SalesCustomerRetentionDetailPageProps = {}) {
  const { id: routeId } = useParams<{ id: string }>()
  const id = retentionRecordIdProp ?? routeId
  const readOnly = readOnlyProp
  const navigate = useNavigate()
  const location = useLocation()
  const qc = useQueryClient()

  const [activeTab, setActiveTab] = useState(0)
  const [emailOpen, setEmailOpen] = useState(false)
  const [emailTo, setEmailTo] = useState<string[]>([])
  const [contactPickerOpen, setContactPickerOpen] = useState(false)
  const [callPickerOpen, setCallPickerOpen] = useState(false)
  const [headerCreateEventOpen, setHeaderCreateEventOpen] = useState(false)
  const [reassignOpen, setReassignOpen] = useState(false)

  const isGuid = id !== undefined && id.length === 36 && id.includes('-')
  // Retention DB record ID — set after upsert on load
  const [retentionRecordId, setRetentionRecordId] = useState<string | null>(() => {
    return isGuid ? id : null
  })

  useEffect(() => {
    if (retentionRecordIdProp) {
      setRetentionRecordId(retentionRecordIdProp)
    }
  }, [retentionRecordIdProp])

  const [modalOpen, setModalOpen] = useState(false)
  const [editTask, setEditTask] = useState<any | null>(null)

  const { hasAction } = usePermission()
  const canManageStage     = hasAction('/sales', 'manage')
  const canAssign          = hasAction('/sales', 'assign')

  // ── Privilege action keys ──────────────────────────────────────────────────
  const canCallLog         = hasAction('/sales', 'call_log')
  const canViewCalls       = hasAction('/sales', 'call_history')
  const canCallDownload    = hasAction('/sales', 'call_download')
  const canCallTranscript  = hasAction('/sales', 'call_transcript')
  const canComposeMail     = hasAction('/sales', 'compose_mail')
  const canViewMail        = hasAction('/sales', 'view_mail')
  const canAddComment      = hasAction('/sales', 'add_comment')
  const canViewComments    = hasAction('/sales', 'view_comments')
  const canAddTask         = hasAction('/sales', 'add_task')
  const canViewTasks       = hasAction('/sales', 'view_tasks')
  const canAddEvent        = hasAction('/sales', 'add_event')
  const canViewEvents      = hasAction('/sales', 'view_events')
  const canAddAttachment   = hasAction('/sales', 'add_attachment')
  const canViewAttachments = hasAction('/sales', 'view_attachments')
  const canUpdatePipeline  = hasAction('/sales', 'update_pipeline')
  const canConvert         = hasAction('/sales', 'convert')

  const customerFromState = location.state?.customer as AiRecencyCustomer | undefined

  const { data: dbCustomer, isLoading: isDbLoading } = useQuery({
    queryKey: ['db-retention-customer', retentionRecordId],
    queryFn: () => retentionApi.getById(retentionRecordId!),
    enabled: !!retentionRecordId,
  })

  const customer = useMemo(() => {
    if (customerFromState) {
      return customerFromState
    }
    if (dbCustomer) {
      return {
        outlet_id: dbCustomer.outletId,
        customer_name: dbCustomer.customerName,
        mobile: dbCustomer.mobile,
        email: dbCustomer.email,
        recency_days: dbCustomer.recencyDays,
        last_order_date: dbCustomer.lastOrderDate,
        customer_type_name: dbCustomer.customerType ?? null,
        customer_type_id: dbCustomer.customerTypeId ?? null,
        warehouse_id: dbCustomer.warehouseId ?? null,
        warehouse_name: dbCustomer.warehouseName ?? null,
      } as unknown as AiRecencyCustomer
    }
    return undefined
  }, [customerFromState, dbCustomer])

  const matchedCustomer = null
  const matchedSalesRecord = null

  const defaultStages = useMemo(() => [
    { id: 1, name: 'New', position: 1, type: 'active', color: '#0369a1', isDefault: true, createdAt: '', updatedAt: '' },
    { id: 2, name: 'Onboarded', position: 2, type: 'active', color: '#c2410c', isDefault: false, createdAt: '', updatedAt: '' },
    { id: 3, name: 'Order Placed', position: 3, type: 'active', color: '#047857', isDefault: false, createdAt: '', updatedAt: '' },
    { id: 4, name: 'Won', position: 4, type: 'terminal', color: '#16a34a', isDefault: false, createdAt: '', updatedAt: '' },
    { id: 5, name: 'Lost', position: 5, type: 'terminal', color: '#dc2626', isDefault: false, createdAt: '', updatedAt: '' },
  ], [])

  const stagesToUse = useMemo(() => {
    return defaultStages
  }, [defaultStages])

  // Local states for mock mode
  const [mockStageId, setMockStageId] = useState<number | null>(null)
  const [mockOwner, setMockOwner] = useState<{ id: string; name: string }>({ id: '', name: 'Unassigned' })
  const [mockOnboardUserId, setMockOnboardUserId] = useState<string | null>(null)
  const [mockOrderId, setMockOrderId] = useState<string | null>(null)

  // (mock local state removed — all sub-resources now use real retention API)

  // Onboarding callback simulation states removed

  // Change Stage Dropdown Anchor
  const [stageMenuAnchor, setStageMenuAnchor] = useState<null | HTMLElement>(null)

  const isSevere = customer?.recency_days != null && customer.recency_days >= 90
  const isModerate = customer?.recency_days != null && customer.recency_days >= 60


  const isUpsertingRef = useRef(false)

  // Upsert retention customer in DB when customer data is available.
  // Key = OutletId + PhoneNumber. If customer was Converted (ordered) and reappears,
  // the backend reactivates the existing record preserving full history.
  useEffect(() => {
    if (!customer || dbCustomer || retentionRecordId || isUpsertingRef.current) return
    isUpsertingRef.current = true

    const lastOrderDateIso = customer.last_order_date
      ? new Date(customer.last_order_date).toISOString()
      : null
    retentionApi.upsert({
      outletId:      String(customer.outlet_id),
      customerName:  customer.customer_name,
      mobile:        customer.mobile  ?? null,
      email:         customer.email   ?? null,
      recencyDays:   customer.recency_days,
      lastOrderDate: lastOrderDateIso,
      warehouseId:   customer.warehouse_id   ?? null,
      warehouseName: customer.warehouse_name ?? null,
    }).then(record => {
      setRetentionRecordId(record.id)
    }).catch(() => {
      // fallback: find existing record by outletId + phone
      retentionApi.getByOutletId(String(customer.outlet_id), customer.mobile ?? null).then(record => {
        if (record) setRetentionRecordId(record.id)
      })
    }).finally(() => {
      isUpsertingRef.current = false
    })
  }, [customer?.outlet_id, dbCustomer, retentionRecordId])

  // Initialize mockOwner with the actual owner from dbCustomer
  useEffect(() => {
    if (dbCustomer) {
      setMockOwner({
        id: dbCustomer.ownerId || '',
        name: (dbCustomer as any).ownerName || 'Unassigned',
      })
    }
  }, [dbCustomer])

  const resolvedStage = useMemo(() => {
    if (mockStageId != null) {
      return stagesToUse.find(s => s.id === mockStageId) || stagesToUse[0]
    }
    const onboardingStage = stagesToUse.find(s => s.name.toLowerCase() === 'onboarding')
    return onboardingStage || stagesToUse[0]
  }, [mockStageId, stagesToUse])

  const mockSalesRecord = useMemo<SalesRecord | null>(() => {
    if (!customer) return null
    const customerUserIdValue = mockOnboardUserId !== null ? mockOnboardUserId : String(customer.outlet_id)

    // Resolve orderId from dbCustomer or local state
    const effectiveOrderId = dbCustomer?.orderId ?? mockOrderId

    // Resolve order value from dbCustomer (parsed from string)
    const rawValue = dbCustomer?.orderValue ?? (dbCustomer as any)?.order_value
    const parsedValue = rawValue ? parseFloat(rawValue) : 0
    const effectiveValue = isNaN(parsedValue) ? 0 : parsedValue

    // Customer is placed/won if status is Converted or has an orderId or non-empty orderValue
    const isConvertedOrPlaced = dbCustomer?.status === 'Converted' || !!effectiveOrderId || (dbCustomer?.orderValue != null && dbCustomer?.orderValue !== '')
    const effectiveStage = isConvertedOrPlaced ? 'Won' : (resolvedStage?.name || 'New')

    // Won Date (convertedAt / orderedAt / lastOrderDate)
    const effectiveWonDate = dbCustomer?.orderedAt ?? dbCustomer?.convertedAt ?? dbCustomer?.lastOrderDate ?? customer.last_order_date ?? new Date().toISOString()

    return {
      id: retentionRecordId ?? `mock-retention-${customer.outlet_id}`,
      dealId: `mock-deal-${customer.outlet_id}`,
      dealName: customer.customer_name,
      value: effectiveValue,
      ownerId: mockOwner.id,
      ownerName: mockOwner.name,
      orgId: '',
      lobId: null,
      warehouseId: null,
      company: null,
      contactName: customer.customer_name,
      sourceDealStage: effectiveStage,
      status: effectiveStage as any,
      createdAt: customer.last_order_date || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      closeDate: effectiveWonDate,
      onboard: {
        id: `mock-onboard-${customer.outlet_id}`,
        customerUserId: customerUserIdValue,
        orderId: effectiveOrderId ?? null,
        createdAt: effectiveWonDate,
        updatedAt: new Date().toISOString(),
      },
      deal: {
        id: `mock-deal-${customer.outlet_id}`,
        title: customer.customer_name,
        amount: effectiveValue,
        currency: 'INR',
        priority: 'MEDIUM',
        stage: effectiveStage,
        source: 'AI Retention',
        description: `Retention record for customer ${customer.customer_name} (outlet_id: ${customer.outlet_id}).`,
        expectedCloseDate: null,
        customerId: `mock-customer-${customer.outlet_id}`,
        contactId: `mock-contact-${customer.outlet_id}`,
        ownerId: mockOwner.id,
        orgId: null,
        createdAt: customer.last_order_date || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        customer: {
          id: `mock-customer-${customer.outlet_id}`,
          name: customer.customer_name,
          companyName: customer.customer_name,
          email: customer.email,
          phone: customer.mobile,
          source: 'AI Retention',
          createdAt: customer.last_order_date || new Date().toISOString(),
        },
        contact: {
          id: `mock-contact-${customer.outlet_id}`,
          name: customer.customer_name,
          email: customer.email,
          phone: customer.mobile,
          role: 'Primary Contact',
          createdAt: customer.last_order_date || new Date().toISOString(),
        },
        owner: {
          id: mockOwner.id,
          name: mockOwner.name,
          email: '',
        },
        orgName: '',
        lobName: '',
        warehouseName: '',
      } as any
    }
  }, [customer, mockOwner, resolvedStage, mockOnboardUserId, mockOrderId, dbCustomer, retentionRecordId])

  // Active salesRecord reference
  const salesRecord = useMemo(() => {
    return mockSalesRecord
  }, [mockSalesRecord])

  const isOrderPlaced = salesRecord?.onboard?.orderId != null

  const salesId = salesRecord?.id

  // Retention customers always use the AI recency data for contact info
  const contactsForCall = useMemo(() => {
    return [{
      id: `retention-contact-${customer?.outlet_id}`,
      name: customer?.customer_name || '',
      phone: customer?.mobile || '',
      email: customer?.email || '',
      role: 'Primary Contact',
      createdAt: new Date().toISOString(),
    }]
  }, [customer])

  // ── Activities for Timeline ──
  const { data: activities = [], isLoading: loadingActs } = useQuery({
    queryKey: ['retention-activities', retentionRecordId],
    queryFn: () => retentionApi.getActivities(retentionRecordId!),
    enabled: !!retentionRecordId,
    staleTime: 30_000,
  })

  // ── Status Progress / Status Transition Mutation ──
  const updateStageMut = useMutation({
    mutationFn: (status: 'New' | 'Onboarded' | 'Won' | 'Lost') => salesApi.updateSaleStatus(salesId!, status),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales-record', salesId] })
      qc.invalidateQueries({ queryKey: ['sales-activities', salesId] })
      toast.success('Sale status updated successfully')
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to update status')
  })

  // ── Notes / Comments Mutations ──
  const { data: notes = [], isLoading: loadingNotes } = useQuery({
    queryKey: ['retention-notes', retentionRecordId],
    queryFn: () => retentionApi.getNotes(retentionRecordId!),
    enabled: !!retentionRecordId && activeTab === 3,
  })

  const addNoteMut = useMutation({
    mutationFn: (content: string) => {
      if (!retentionRecordId) return Promise.resolve([] as RetentionNote[])
      return retentionApi.addNote(retentionRecordId, content)
    },
    onSuccess: (newNotes) => {
      qc.setQueryData(['retention-notes', retentionRecordId], newNotes)
      qc.invalidateQueries({ queryKey: ['retention-activities', retentionRecordId] })
      toast.success('Comment added')
    },
  })
  const updateNoteMut = useMutation({
    mutationFn: ({ noteId, content }: { noteId: string; content: string }) => {
      if (!retentionRecordId) return Promise.resolve([] as RetentionNote[])
      return retentionApi.updateNote(retentionRecordId, noteId, content)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['retention-notes', retentionRecordId] })
      toast.success('Comment updated')
    },
  })
  const deleteNoteMut = useMutation({
    mutationFn: (noteId: string) => {
      if (!retentionRecordId) return Promise.resolve()
      return retentionApi.deleteNote(retentionRecordId, noteId)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['retention-notes', retentionRecordId] })
      qc.invalidateQueries({ queryKey: ['retention-activities', retentionRecordId] })
      toast.success('Comment deleted')
    },
  })

  // Note: calls are no longer manually re-logged here after the dialer finishes.
  // callStore's saveOutcomeForRetention() already persists the CallLog + the
  // matching RetentionActivity entry (see SaveCallOutcomeCommand on the backend),
  // exactly like Leads/Deals/Contacts — logging it again here was creating a
  // second, duplicate record for every call.

  // ── Reassign mutation ──
  const reassignMut = useMutation({
    mutationFn: async (user: AdminUser) => {
      const payload = [{
        outletId: String(customer!.outlet_id),
        customerName: customer!.customer_name,
        mobile: customer!.mobile || null,
        email: customer!.email || null,
        recencyDays: customer!.recency_days,
        lastOrderDate: customer!.last_order_date ? new Date(customer!.last_order_date).toISOString() : null,
      }]
      await retentionApi.bulkAssign(payload, user.id)
      return user
    },
    onSuccess: (user) => {
      setMockOwner({ id: user.id, name: user.name })
      qc.invalidateQueries({ queryKey: ['db-retention-customer', id] })
      qc.invalidateQueries({ queryKey: ['retention-activities', retentionRecordId] })
      qc.invalidateQueries({ queryKey: ['local-retention-customers-lookup'] })
      toast.success('Owner reassigned successfully')
      setReassignOpen(false)
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Failed to reassign owner')
    }
  })

  // ── Send Email mutation ──
  const sendEmailMut = useMutation({
    mutationFn: (data: SendEmailData) => emailApi.send({
      entityType: 'RETENTION',
      entityId: retentionRecordId!,
      to: data.to,
      cc: data.cc.length > 0 ? data.cc : undefined,
      bcc: data.bcc.length > 0 ? data.bcc : undefined,
      subject: data.subject,
      htmlBody: data.body,
      files: data.attachments.length > 0 ? data.attachments : undefined,
    }),
    onSuccess: () => {
      toast.success('Email sent')
      setEmailOpen(false)
      if (retentionRecordId) {
        qc.invalidateQueries({ queryKey: ['retention-activities', retentionRecordId] })
        qc.invalidateQueries({ queryKey: ['emails', 'RETENTION', retentionRecordId] })
      }
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to send email'),
  })

  // Onboarding callback simulation mutations removed

  const handleStageChange = (stageId: number) => {
    setMockStageId(stageId)
    toast.success('Sale stage updated')
    setStageMenuAnchor(null)
  }

  const handleReassign = (user: AdminUser) => {
    reassignMut.mutate(user)
  }



  // Render isLoading states
  const isLoadingData = isDbLoading

  if (!customer) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '60vh', gap: 1 }}>
        <CircularProgress size={24} />
        <Typography fontSize="13px" color="text.secondary">Fetching Retention Customer...</Typography>
      </Box>
    )
  }

  const deal = salesRecord?.deal
  const owner = deal?.owner ?? { id: salesRecord?.ownerId || '', name: salesRecord?.ownerName || 'Unassigned' }

  const handleLogCall = () => {
    if (customer?.mobile) {
      callStore.showPlaceCall({
        entityType: 'RETENTION',
        entityId: retentionRecordId!,
        name: customer.customer_name,
        phone: customer.mobile,
        companyName: customer.customer_name,
      })
    } else {
      setCallPickerOpen(true)
    }
  }

  // ── Tabs Setup ──
  const salesTabs = [
    {
      label: 'Activity',
      content: (
        <UniversalActivityTimeline
          logs={activities}
          isLoading={loadingActs || !retentionRecordId}
          entityType="sales"
        />
      ),
    },
    ...(canViewCalls ? [{
      label: 'Call',
      content: retentionRecordId ? (
        <RetentionCallsSection retentionId={retentionRecordId} onLogCall={handleLogCall} canModify={!readOnly && canCallLog} />
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={24} /></Box>
      ),
    }] : []),
    ...(canViewMail ? [{
      label: 'Email',
      content: retentionRecordId ? (
        <EmailHistorySection
          entityType="RETENTION"
          entityId={retentionRecordId}
          canModify={!readOnly && canComposeMail}
          onCompose={() => setContactPickerOpen(true)}
        />
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={24} /></Box>
      ),
    }] : []),
    ...(canViewComments ? [{
      label: 'Comments',
      content: (
        <Box>
          {canAddComment && !readOnly && <CommentInput onAdd={async c => { await addNoteMut.mutateAsync(c) }} isAdding={addNoteMut.isPending} />}
          {loadingNotes ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
          ) : notes.length === 0 ? (
            <CommentsEmptyState />
          ) : (
            <Stack spacing={0}>
              {notes.map((note, idx) => (
                <CommentItem
                  key={note.id}
                  note={note}
                  onUpdate={!readOnly && canAddComment ? async c => { await updateNoteMut.mutateAsync({ noteId: note.id, content: c }) } : undefined}
                  onDelete={!readOnly && canAddComment ? () => deleteNoteMut.mutate(note.id) : undefined}
                  isUpdating={updateNoteMut.isPending}
                  isLast={idx === notes.length - 1}
                />
              ))}
            </Stack>
          )}
        </Box>
      ),
    }] : []),
    ...(canViewTasks ? [{
      label: 'Tasks',
      content: retentionRecordId ? (
        <RetentionTasksSection retentionId={retentionRecordId} canModify={!readOnly && canAddTask} />
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={24} /></Box>
      ),
    }] : []),
    ...(canViewEvents ? [{
      label: 'Events',
      content: retentionRecordId ? (
        <RetentionEventsSection
          retentionId={retentionRecordId}
          canModify={!readOnly && canAddEvent}
          externalCreateOpen={headerCreateEventOpen}
          onExternalCreateClose={() => setHeaderCreateEventOpen(false)}
        />
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={24} /></Box>
      ),
    }] : []),
    ...(canViewAttachments ? [{
      label: 'Attachments',
      content: retentionRecordId ? (
        <RetentionAttachmentsSection retentionId={retentionRecordId} canModify={!readOnly && canAddAttachment} />
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={24} /></Box>
      ),
    }] : []),
  ]

  return (
    <Box sx={{
      height: '100%', overflowY: 'auto', bgcolor: '#f5f6fa',
      '&::-webkit-scrollbar': { width: '5px' },
      '&::-webkit-scrollbar-thumb': { bgcolor: '#d1d5db', borderRadius: '4px' },
    }}>

      {/* ── Breadcrumb ── */}
      {!readOnly && (
        <Box sx={{ px: 3, pt: 2, pb: 0, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconButton size="small" onClick={() => navigate('/customer-retention')} sx={{ color: '#6b7280', mr: 0.5 }}>
            <BackIcon fontSize="small" />
          </IconButton>
          <Typography fontSize="13px" color="text.secondary"
            sx={{ cursor: 'pointer', '&:hover': { color: 'text.primary' } }}
            onClick={() => navigate('/customer-retention')}>
            Retention Customers
          </Typography>
          <Typography fontSize="13px" color="text.secondary" sx={{ mx: 0.25 }}>/</Typography>
          <Typography fontSize="13px" fontWeight={600} color="text.primary">{salesRecord.dealName}</Typography>
          <Box sx={{ flex: 1 }} />
        </Box>
      )}

      {/* ── Hero Card ── */}
      <Box sx={{ px: 3, pt: 1.5, pb: 0 }}>
        <Paper elevation={0} sx={{ border: `2px solid ${themeColors.primary}45`, borderRadius: '14px', p: 2.5, bgcolor: '#fff' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>

            {/* Avatar */}
            <Avatar sx={{ width: 62, height: 62, bgcolor: '#f0fdf4', color: '#16a34a', flexShrink: 0, boxShadow: '0 4px 12px #16a34a30' }}>
              <WonIcon sx={{ fontSize: 30 }} />
            </Avatar>

            {/* Title + chips */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                <Typography fontWeight={700} fontSize="18px" color="text.primary">{salesRecord.dealName}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, flexWrap: 'wrap' }}>
                <Typography fontSize="12.5px" color="text.secondary" fontWeight={600}>
                  {fmtAmount(salesRecord.value, deal?.currency)}
                </Typography>
                <Chip
                  label={salesRecord.status || 'New'}
                  size="small"
                  sx={{
                    height: 20,
                    borderRadius: '4px',
                    fontSize: '10.5px',
                    fontWeight: 700,
                    bgcolor: getStatusStyle(salesRecord.status || 'New').bg,
                    color: getStatusStyle(salesRecord.status || 'New').color,
                    border: getStatusStyle(salesRecord.status || 'New').border,
                    '& .MuiChip-label': { px: '7px' }
                  }}
                />
                <Typography fontSize="12.5px" color="text.secondary">Won: {fmt(salesRecord.closeDate)}</Typography>
              </Box>
            </Box>

            {/* Action buttons */}
            {!readOnly && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end', flexShrink: 0 }}>
                {canCallLog && (
                  <Button size="small" variant="outlined"
                    startIcon={<PhoneIcon sx={{ fontSize: '14px !important' }} />}
                    disabled={callStore.isStarting}
                    onClick={handleLogCall}
                    sx={{ borderRadius: '8px', textTransform: 'none', fontSize: '12.5px', fontWeight: 600, color: '#374151', borderColor: '#d1d5db', '&:hover': { borderColor: themeColors.primary, color: themeColors.primary } }}>
                    {callStore.isStarting ? <CircularProgress size={13} sx={{ mr: 0.5 }} /> : null}
                    Log Call
                  </Button>
                )}
                {canAddEvent && (
                  <Button size="small" variant="outlined"
                    startIcon={<CalendarIcon sx={{ fontSize: '14px !important' }} />}
                    onClick={() => { setActiveTab(5); setHeaderCreateEventOpen(true) }}
                    sx={{ borderRadius: '8px', textTransform: 'none', fontSize: '12.5px', fontWeight: 600, color: '#374151', borderColor: '#d1d5db', '&:hover': { borderColor: themeColors.primary, color: themeColors.primary } }}>
                    Schedule Meeting
                  </Button>
                )}
                {canComposeMail && (
                  <Button size="small" variant="outlined"
                    startIcon={<EmailIcon sx={{ fontSize: '14px !important' }} />}
                    onClick={() => setContactPickerOpen(true)}
                    sx={{ borderRadius: '8px', textTransform: 'none', fontSize: '12.5px', fontWeight: 600, color: '#374151', borderColor: '#d1d5db', '&:hover': { borderColor: themeColors.primary, color: themeColors.primary } }}>
                    Send Email
                  </Button>
                )}
              </Box>
            )}
          </Box>
        </Paper>
      </Box>

      {/* ── Two-column body ── */}
      <Box sx={{ display: 'flex', gap: 2.5, p: 3, pt: 2, alignItems: 'flex-start' }}>

        {/* ════ Left Sidebar ════ */}
        <Box sx={{ width: 260, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>

          {/* Stage Progress */}


          {/* Onboarding Stage/Status */}
          {salesRecord.onboard && (
            <SideCard
              title="Onboard Stage"
              action={salesRecord.onboard.customerUserId == null ? (
                <Tooltip title="Copy Prefilled Onboarding Link">
                  <IconButton
                    size="small"
                    onClick={() => {
                      const link = `${import.meta.env.VITE_SALES_APP_URL}/customer-onboard/add?salesId=${salesRecord.id}`
                      // const link = `https://blue-moss-031c76000.3.azurestaticapps.net/customer-onboard/add?salesId=${salesRecord.id}`
                      navigator.clipboard.writeText(link)
                      toast.success('Onboarding link copied to clipboard!')
                    }}
                    sx={{
                      color: themeColors.success,
                      p: 0.5,
                      '&:hover': { bgcolor: `${themeColors.success}12` }
                    }}
                  >
                    <LinkIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              ) : undefined}
            >
              <Stack spacing={1.5}>
                {/* Onboarding Status */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography fontSize="12px" fontWeight={600} color="text.secondary">
                    Status
                  </Typography>
                  <Chip
                    label={salesRecord.onboard.customerUserId != null ? 'Completed' : 'Processing'}
                    size="small"
                    sx={{
                      height: 20,
                      borderRadius: '4px',
                      bgcolor: salesRecord.onboard.customerUserId != null ? '#ecfdf5' : '#fef3c7',
                      color: salesRecord.onboard.customerUserId != null ? '#059669' : '#d97706',
                      border: `1px solid ${salesRecord.onboard.customerUserId != null ? '#a7f3d0' : '#fde68a'}`,
                      fontSize: '11px',
                      fontWeight: 600,
                      '& .MuiChip-label': { px: '8px' }
                    }}
                  />
                </Box>

                {/* Redirect to Sales Page */}
                <Box>
                  <Typography fontSize="11px" color="text.secondary" fontWeight={500} sx={{ mb: 0.25 }}>
                    Redirect to Sales Page
                  </Typography>
                  {salesRecord.onboard.customerUserId ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: '#f8fafc', p: 1, borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                      {/* User ID on the left with copy function */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography fontSize="12px" fontWeight={600} sx={{ fontFamily: 'monospace', color: '#0f172a' }}>
                          {salesRecord.onboard.customerUserId}
                        </Typography>
                        <Tooltip title="Copy User ID">
                          <IconButton
                            size="small"
                            onClick={() => {
                              navigator.clipboard.writeText(salesRecord.onboard!.customerUserId!)
                              toast.success('Copied to clipboard!')
                            }}
                            sx={{ p: 0.25 }}
                          >
                            <CopyIcon sx={{ fontSize: 12, color: '#94a3b8' }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      {/* Arrow on the right side for redirection */}
                      <Tooltip title="Redirect to Sales Page">
                        <IconButton
                          size="small"
                          onClick={() => openSalesAppForRetentionCustomer({
                            outletId: salesRecord.onboard!.customerUserId!,
                            customerTypeId: customer?.customer_type_id,
                            customerType: customer?.customer_type_name,
                          })}
                          sx={{
                            p: 0.25,
                            color: themeColors.primary,
                            '&:hover': {
                              color: '#201047',
                              bgcolor: 'rgba(99, 102, 241, 0.08)'
                            }
                          }}
                        >
                          <ArrowForwardIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ) : (
                    <Typography fontSize="12px" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                      Pending system update...
                    </Typography>
                  )}
                </Box>

                {/* Order Placement Status */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pt: 0.5 }}>
                  <Typography fontSize="12px" fontWeight={600} color="text.secondary">
                    Order Status
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Chip
                      label={salesRecord.onboard.orderId != null ? 'Placed' : 'Not Placed'}
                      size="small"
                      sx={{
                        height: 20,
                        borderRadius: '4px',
                        bgcolor: salesRecord.onboard.orderId != null ? '#ecfdf5' : '#f3f4f6',
                        color: salesRecord.onboard.orderId != null ? '#059669' : '#4b5563',
                        border: `1px solid ${salesRecord.onboard.orderId != null ? '#a7f3d0' : '#e5e7eb'}`,
                        fontSize: '11px',
                        fontWeight: 600,
                        '& .MuiChip-label': { px: '8px' }
                      }}
                    />

                  </Box>
                </Box>

                {/* Redirect to Order Page */}
                <Box>
                  <Typography fontSize="11px" color="text.secondary" fontWeight={500} sx={{ mb: 0.25 }}>
                    Redirect to Order Page
                  </Typography>
                  {salesRecord.onboard.orderId ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: '#f8fafc', p: 1, borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                      {/* Order ID on the left with copy function */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography fontSize="12px" fontWeight={600} sx={{ fontFamily: 'monospace', color: '#0f172a' }}>
                          {salesRecord.onboard.orderId}
                        </Typography>
                        <Tooltip title="Copy Order ID">
                          <IconButton
                            size="small"
                            onClick={() => {
                              navigator.clipboard.writeText(salesRecord.onboard!.orderId!)
                              toast.success('Copied to clipboard!')
                            }}
                            sx={{ p: 0.25 }}
                          >
                            <CopyIcon sx={{ fontSize: 12, color: '#94a3b8' }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      {/* Arrow on the right side for redirection */}
                      <Tooltip title="Redirect to Order Page">
                        <IconButton
                          component="a"
                          href={`${import.meta.env.VITE_SALES_APP_URL}/order-detail/${salesRecord.onboard.orderId}`}
                          // href={`https://blue-moss-031c76000.3.azurestaticapps.net/order-detail/${salesRecord.onboard.orderId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          size="small"
                          sx={{
                            p: 0.25,
                            color: themeColors.primary,
                            '&:hover': {
                              color: '#201047',
                              bgcolor: 'rgba(99, 102, 241, 0.08)'
                            }
                          }}
                        >
                          <ArrowForwardIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ) : (
                    <Typography fontSize="12px" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                      {salesRecord.onboard.customerUserId != null 
                        ? 'Waiting for order placement...' 
                        : 'Pending onboarding completion...'}
                    </Typography>
                  )}
                </Box>
              </Stack>
            </SideCard>
          )}

          {/* Sale Info */}
          <SideCard title="Sale Info">
            <Stack spacing={0.5}>
              <InfoRow icon={<AmountIcon sx={{ fontSize: 15 }} />} label="Value"
                value={
                  <Typography fontSize="13px" fontWeight={700} color={themeColors.primary}>
                    {fmtAmount(salesRecord.value, deal?.currency)}
                  </Typography>
                } />
              <InfoRow icon={<CalIcon sx={{ fontSize: 15 }} />} label="Won Date" value={fmt(salesRecord.closeDate)} />

              <InfoRow icon={<InfoIcon sx={{ fontSize: 15 }} />} label="Sales Stage"
                value={
                  <Chip
                    label={salesRecord.status || 'New'}
                    size="small"
                    sx={{
                      height: 20,
                      borderRadius: '4px',
                      bgcolor: getStatusStyle(salesRecord.status || 'New').bg,
                      color: getStatusStyle(salesRecord.status || 'New').color,
                      border: getStatusStyle(salesRecord.status || 'New').border,
                      fontSize: '11px',
                      fontWeight: 600,
                      '& .MuiChip-label': { px: '8px' }
                    }}
                  />
                } />
              {deal?.priority && (
                <InfoRow icon={<InfoIcon sx={{ fontSize: 15 }} />} label="Priority"
                  value={
                    <Chip label={deal.priority} size="small"
                      sx={{ height: 18, fontSize: '10.5px', fontWeight: 700, borderRadius: '4px', bgcolor: '#f3f4f6', color: '#374151', '& .MuiChip-label': { px: '6px' } }} />
                  } />
              )}
              {deal?.source && (
                <InfoRow icon={<InfoIcon sx={{ fontSize: 15 }} />} label="Source" value={deal.source} />
              )}
              {customer?.customer_type_name && (
                <InfoRow icon={<InfoIcon sx={{ fontSize: 15 }} />} label="Customer Type" value={customer.customer_type_name} />
              )}
              {customer?.warehouse_name && (
                <InfoRow icon={<InfoIcon sx={{ fontSize: 15 }} />} label="Warehouse" value={customer.warehouse_name} />
              )}
              <InfoRow icon={<CalIcon sx={{ fontSize: 15 }} />} label="Created" value={fmt(salesRecord.createdAt)} />
            </Stack>
          </SideCard>



          {/* Assigned To */}
          <SideCard title="Assigned To" action={(!readOnly && canAssign) ?
            <Tooltip title={isOrderPlaced ? "Cannot reassign sale after order is placed" : "Reassign owner"}>
              <span>
                <IconButton size="small" onClick={() => setReassignOpen(true)}
                  disabled={reassignMut.isPending || isOrderPlaced}
                  sx={{ color: '#9ca3af', '&:hover': { color: themeColors.primary, bgcolor: '#f5f7fb' } }}>
                  {reassignMut.isPending ? <CircularProgress size={13} /> : <EditIcon sx={{ fontSize: 14 }} />}
                </IconButton>
              </span>
            </Tooltip>
          : undefined}>
            <Box sx={{ p: 1, borderRadius: '10px', border: `1px solid ${themeColors.border}`, bgcolor: '#fafafa', transition: 'all 0.15s', '&:hover': { borderColor: themeColors.primary, bgcolor: '#f5f7fb' } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar sx={{ width: 28, height: 28, fontSize: '11px', fontWeight: 700, bgcolor: avatarColor(owner.name), flexShrink: 0 }}>
                  {initials(owner.name)}
                </Avatar>
                <Typography fontSize="13px" fontWeight={700} color={themeColors.primary} noWrap>{owner.name}</Typography>
              </Box>
            </Box>
            {(!readOnly && canAssign) && (
              <Box onClick={() => !isOrderPlaced && setReassignOpen(true)}
                sx={{
                  mt: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.5,
                  py: 0.6,
                  borderRadius: '8px',
                  border: isOrderPlaced ? '1.5px dashed #ccc' : `1.5px dashed ${themeColors.primary}55`,
                  cursor: isOrderPlaced ? 'not-allowed' : 'pointer',
                  color: isOrderPlaced ? '#9ca3af' : themeColors.primary,
                  transition: 'all 0.15s',
                  opacity: isOrderPlaced ? 0.6 : 1,
                  '&:hover': isOrderPlaced ? {} : { bgcolor: '#f5f7fb', borderColor: themeColors.primary }
                }}>
                <EditIcon sx={{ fontSize: 12 }} />
                <Typography fontSize="11px" fontWeight={600}>Reassign Sale</Typography>
              </Box>
            )}

            <HelpAssigneeSelector
              currentPath="/customer-retention"
              lobId={dbCustomer?.lobId ?? salesRecord?.lobId}
              currentHelpAssigneeId={dbCustomer?.helpAssigneeId ?? (typeof salesRecord?.helpAssignee === 'object' && salesRecord?.helpAssignee !== null ? (salesRecord.helpAssignee as any).id : salesRecord?.helpAssigneeId)}
              currentHelpAssigneeName={dbCustomer?.helpAssigneeName ?? (typeof salesRecord?.helpAssignee === 'object' && salesRecord?.helpAssignee !== null ? (salesRecord.helpAssignee as any).name : (salesRecord?.helpAssignee as string))}
              onUpdate={async (newId) => {
                if (dbCustomer) {
                  await retentionApi.updateHelpAssignee(dbCustomer.id, newId)
                  qc.invalidateQueries({ queryKey: ['db-retention-customer', retentionRecordId] })
                  qc.invalidateQueries({ queryKey: ['retention-activities', retentionRecordId] })
                } else if (salesRecord) {
                  await salesApi.updateHelpAssignee(salesRecord.id, newId)
                  qc.invalidateQueries({ queryKey: ['sales', id] })
                  qc.invalidateQueries({ queryKey: ['sales-activities', id] })
                }
              }}
            />
          </SideCard>

          {deal?.description && (
            <SideCard title="Description">
              <Typography fontSize="12.5px" color="text.primary" sx={{ lineHeight: 1.65, whiteSpace: 'pre-wrap' }}>
                {deal.description}
              </Typography>
            </SideCard>
          )}

          {/* Business / LOB / Warehouse */}
          <SideCard title="Business Details">
            <Stack spacing={0.5}>
              <InfoRow
                icon={<OrgIcon sx={{ fontSize: 15 }} />}
                label="Business"
                value={
                  <Typography
                    fontSize="13px"
                    fontWeight={600}
                    color={
                      deal?.orgName || null
                        ? 'text.primary'
                        : 'text.disabled'
                    }
                  >
                    {deal?.orgName || null || '—'}
                  </Typography>
                }
              />
              <InfoRow
                icon={<LobIcon sx={{ fontSize: 15 }} />}
                label="LOB"
                value={
                  <Typography
                    fontSize="13px"
                    fontWeight={600}
                    color={
                      deal?.lobName || null
                        ? 'text.primary'
                        : 'text.disabled'
                    }
                  >
                    {deal?.lobName || null || '—'}
                  </Typography>
                }
              />
            </Stack>
          </SideCard>

          {/* Quick navigation */}
          <SideCard>
            {[
              { icon: <TimelineIcon sx={{ fontSize: 16 }} />, label: 'Activity', tab: 0 },
              { icon: <PhoneIcon    sx={{ fontSize: 16 }} />, label: 'Call',     tab: 1 },
              { icon: <NoteIcon     sx={{ fontSize: 16 }} />, label: 'Comments', tab: 3 },
              { icon: <TaskIcon     sx={{ fontSize: 16 }} />, label: 'Tasks',    tab: 4 },
              { icon: <CalendarIcon sx={{ fontSize: 16 }} />, label: 'Events',   tab: 5 },
            ].map(item => (
              <Box key={item.label} onClick={() => setActiveTab(item.tab)}
                sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, borderRadius: '8px', cursor: 'pointer', '&:hover': { bgcolor: '#f5f7fb' } }}>
                <Box sx={{ color: themeColors.primary }}>{item.icon}</Box>
                <Typography fontSize="12.5px" fontWeight={600} sx={{ flex: 1 }}>{item.label}</Typography>
              </Box>
            ))}
          </SideCard>

        </Box>

        {/* ════ Right Panel ════ */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <EntityDetailTabs
            tabs={salesTabs}
            activeTab={activeTab}
            onChange={setActiveTab}
            padding={2.5}
            tabHeight={44}
            tabFontSize="13px"
            tabFontWeight={600}
            tabPaddingX={2}
            borderRadius="12px"
          />
        </Box>

      </Box>

      {/* ── Modals ── */}
      {reassignOpen && salesRecord && (
        <ReassignOwnerModal
          open={reassignOpen}
          currentOwnerId={owner.id}
          orgId={deal?.orgId ?? salesRecord.orgId}
          onClose={() => setReassignOpen(false)}
          onSelect={user => handleReassign(user)}
        />
      )}



      <SelectCallContactModal
        open={callPickerOpen}
        customerId={deal?.customerId ?? null}
        customerName={customer?.customer_name}
        customerPhone={customer?.mobile}
        onClose={() => setCallPickerOpen(false)}
        onSelect={contact => {
          setCallPickerOpen(false)
          callStore.showPlaceCall({
            entityType: 'RETENTION',
            entityId: retentionRecordId!,
            name: contact.name || customer?.customer_name || '',
            phone: contact.phone || customer?.mobile || '',
            companyName: customer?.customer_name ?? undefined,
          })
        }}
      />

      <SelectEmailContactModal
        open={contactPickerOpen}
        customerId={deal?.customerId ?? null}
        customerName={customer?.customer_name}
        customerEmail={customer?.email}
        onClose={() => setContactPickerOpen(false)}
        onSelect={emails => {
          setEmailTo(emails)
          setContactPickerOpen(false)
          setEmailOpen(true)
        }}
      />

      <SendEmailDrawer
        open={emailOpen}
        onClose={() => { setEmailOpen(false); setEmailTo([]) }}
        defaultTo={emailTo}
        onSend={data => sendEmailMut.mutate(data)}
        isSending={sendEmailMut.isPending}
        orgId={deal?.orgId ?? salesRecord.orgId}
      />


    </Box>
  )
})

export default SalesCustomerRetentionDetailPage
